import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

export default function PaybackPeriodCalc() {
  const [mode, setMode] = useState('fixed');
  return (
    <CalcShell look="cnet" title="Payback Period Calculator" subtitle="Compute the number of years to recover an initial investment from fixed or irregular annual cash flows."
      about={<><p>The <b>payback period</b> is the time required for an investment to generate cash inflows equal to the initial outflow. Simple but ignores time value of money.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setMode('fixed')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='fixed'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Fixed Cash Flow</button>
        <button onClick={()=>setMode('irregular')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='irregular'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Irregular Cash Flow Each Year</button>
        <button onClick={()=>setMode('discounted')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='discounted'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Discounted Payback</button>
      </div>
      {mode === 'fixed' ? <Fixed/> : mode === 'irregular' ? <Irregular/> : <Discounted/>}
    </CalcShell>
  );
}

function Discounted() {
  const [initial, setInitial] = useState(30000);
  const [flows, setFlows] = useState([5000, 7000, 9000, 12000, 14000]);
  const [rate, setRate] = useState(10);
  const update = (i, v) => setFlows(prev => prev.map((f, idx) => idx === i ? v : f));
  const addYear = () => setFlows(prev => [...prev, 0]);
  const c = useMemo(() => {
    let cum = 0;
    const disc = [];
    for (let i = 0; i < flows.length; i++) {
      const d = flows[i] / Math.pow(1 + rate/100, i + 1);
      disc.push({ year: i + 1, flow: flows[i], discounted: d, cum: cum + d });
      if (cum + d >= initial) {
        const yearsPart = i + (initial - cum) / d;
        return { years: yearsPart, disc };
      }
      cum += d;
    }
    return { years: Infinity, disc };
  }, [initial, flows, rate]);
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl mb-3">
        <div className="field"><label>Initial investment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
        <div className="field"><label>Discount rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
      </div>
      <table className="w-full text-sm mb-4 border border-slate-200">
        <thead className="bg-blue-800 text-white"><tr><th className="px-2 py-2 text-left">Year</th><th className="px-2 py-2 text-right">Cash flow</th><th className="px-2 py-2 text-right">Discounted flow</th><th className="px-2 py-2 text-right">Cumulative discounted</th></tr></thead>
        <tbody>
          {flows.map((f, i) => (
            <tr key={i} className={i%2?'bg-slate-50':''}>
              <td className="px-2 py-2">Year {i+1}</td>
              <td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={f} onChange={e=>update(i, +e.target.value||0)} className="!py-1 flex-1"/></div></td>
              <td className="px-2 py-2 text-right">{fmt(c.disc[i]?.discounted || 0)}</td>
              <td className="px-2 py-2 text-right font-semibold">{fmt(c.disc[i]?.cum || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addYear} className="text-blue-700 text-sm font-semibold">+ Add another year</button>
      <div className="result-panel mt-6"><div className="result-title">Discounted payback period</div><div className="result-value" data-testid="pb-discounted">{isFinite(c.years) ? `${c.years.toFixed(3)} years` : 'Never recovers'}</div></div>
    </div>
  );
}

function Fixed() {
  const [initial, setInitial] = useState(100000);
  const [flow, setFlow] = useState(30000);
  const [growthDir, setGrowthDir] = useState('Increase');
  const [growthRate, setGrowthRate] = useState(5);
  const [numYears, setNumYears] = useState(5);
  const [discRate, setDiscRate] = useState(10);

  const c = useMemo(() => {
    const g = (growthDir === 'Decrease' ? -1 : 1) * growthRate / 100;
    const d = discRate / 100;
    const rows = [];
    let cum = 0, cumDisc = 0;
    let paybackYears = Infinity, discPaybackYears = Infinity;
    for (let i = 1; i <= numYears; i++) {
      const cf = flow * Math.pow(1 + g, i - 1);
      const dcf = cf / Math.pow(1 + d, i);
      const nextCum = cum + cf;
      const nextCumDisc = cumDisc + dcf;
      if (!isFinite(paybackYears) && nextCum >= initial) {
        paybackYears = (i - 1) + (initial - cum) / cf;
      }
      if (!isFinite(discPaybackYears) && nextCumDisc >= initial) {
        discPaybackYears = (i - 1) + (initial - cumDisc) / dcf;
      }
      cum = nextCum;
      cumDisc = nextCumDisc;
      rows.push({ year: i, flow: cf, cumulative: cum, discounted: dcf, cumDisc });
    }
    const totalUndiscounted = cum;
    const avgAnnual = totalUndiscounted / numYears;
    const totalReturnPct = initial > 0 ? (totalUndiscounted - initial) / initial * 100 : 0;
    return { rows, paybackYears, discPaybackYears, avgAnnual, totalUndiscounted, totalReturnPct };
  }, [initial, flow, growthDir, growthRate, numYears, discRate]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="field"><label>Initial Investment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1" data-testid="pb-fixed-initial"/></div></div>
        <div className="field"><label>Cash Flow</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={flow} onChange={e=>setFlow(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">/Year</span></div></div>
        <div className="field"><label>Growth</label>
          <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
            <select value={growthDir} onChange={e=>setGrowthDir(e.target.value)}><option>Increase</option><option>Decrease</option></select>
            <div className="flex gap-2"><input type="number" step="0.01" value={growthRate} onChange={e=>setGrowthRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%/Year</span></div>
          </div>
        </div>
        <div className="field"><label>Number of Years</label><input type="number" value={numYears} onChange={e=>setNumYears(+e.target.value||0)} data-testid="pb-fixed-years"/></div>
        <div className="field"><label>Discount Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={discRate} onChange={e=>setDiscRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
      </div>
      <div>
        <div className="cnet-result-hdr">Result</div>
        <div className="cnet-result-body">
          <table className="w-full text-sm mb-3">
            <tbody>
              <tr className="border-b"><td className="py-2 font-semibold">Payback Period</td><td className="text-right font-bold" data-testid="pb-fixed">{isFinite(c.paybackYears) ? `${c.paybackYears.toFixed(3)} years` : 'Never recovers'}</td></tr>
              <tr className="border-b"><td className="py-2">Discounted Payback Period</td><td className="text-right">{isFinite(c.discPaybackYears) ? `${c.discPaybackYears.toFixed(3)} years` : 'Never recovers'}</td></tr>
              <tr className="border-b"><td className="py-2">Average Annual Cash Flow</td><td className="text-right">{fmt(c.avgAnnual)}</td></tr>
              <tr className="border-b"><td className="py-2">Total Cash Received</td><td className="text-right">{fmt(c.totalUndiscounted)}</td></tr>
              <tr><td className="py-2">Total Return</td><td className={`text-right font-semibold ${c.totalReturnPct >= 0 ? 'text-green-700' : 'text-red-600'}`}>{c.totalReturnPct.toFixed(2)}%</td></tr>
            </tbody>
          </table>
          <div className="max-h-64 overflow-auto border border-slate-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-2 py-1 text-left">Year</th><th className="px-2 py-1 text-right">Cash Flow</th><th className="px-2 py-1 text-right">Cumulative</th><th className="px-2 py-1 text-right">Discounted</th></tr></thead>
              <tbody>
                {c.rows.map((r,i)=>(<tr key={i} className={i%2?'bg-slate-50':''}><td className="px-2 py-1">Year {r.year}</td><td className="px-2 py-1 text-right">{fmt(r.flow)}</td><td className="px-2 py-1 text-right">{fmt(r.cumulative)}</td><td className="px-2 py-1 text-right">{fmt(r.discounted)}</td></tr>))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Irregular() {
  const [initial, setInitial] = useState(30000);
  const [flows, setFlows] = useState([5000, 7000, 9000, 12000, 14000]);
  const update = (i, v) => setFlows(prev => prev.map((f, idx) => idx === i ? v : f));
  const addYear = () => setFlows(prev => [...prev, 0]);
  const c = useMemo(() => {
    let cum = 0;
    for (let i = 0; i < flows.length; i++) {
      if (cum + flows[i] >= initial) {
        return { years: i + (initial - cum) / flows[i] };
      }
      cum += flows[i];
    }
    return { years: Infinity };
  }, [initial, flows]);
  return (
    <div>
      <div className="field mb-3"><label>Initial investment</label><div className="flex gap-2 max-w-xs"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
      <table className="w-full text-sm mb-4">
        <thead className="bg-slate-100"><tr><th className="px-2 py-2 text-left">Year</th><th className="px-2 py-2 text-right">Cash flow</th></tr></thead>
        <tbody>
          {flows.map((f, i) => (<tr key={i} className="border-b"><td className="px-2 py-2">Year {i+1}</td><td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={f} onChange={e=>update(i, +e.target.value||0)} className="!py-1 flex-1"/></div></td></tr>))}
        </tbody>
      </table>
      <button onClick={addYear} className="text-blue-700 text-sm font-semibold">+ Add another year</button>
      <div className="result-panel mt-6"><div className="result-title">Payback period</div><div className="result-value">{isFinite(c.years) ? `${c.years.toFixed(3)} years` : 'Never recovers'}</div></div>
    </div>
  );
}
